import { planningSourceCaptureSchema } from 'features/libra/inspectionPlanning/planningTypes'
import { ZonesSectionSchema } from 'features/libra/sections/zonesSection/zonesSectionTypes'
import {
  ZONE_ANALYSIS_STEP_COMPONENT,
  zoneAnalysisConfigSchema,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { FieldAccessScopePath } from 'features/libra/ticket/useScopedFieldAccess'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

const CONDITIONAL_FORM_STEP_COMPONENT = 'ConditionalForm'
const NII_PLANNING_PREPARATION_STEP_COMPONENT = 'NiiPlanningPreparation'

type TicketVersionZoneFieldsReason =
  | 'workflow-unavailable'
  | 'workflow-mismatch'
  | 'invalid-config'

export type TicketVersionZoneFieldsResult =
  | {
      status: 'ready'
      zoneFieldTargets: readonly TicketVersionZoneFieldTarget[]
    }
  | { status: 'unavailable'; reason: TicketVersionZoneFieldsReason }

export type TicketVersionZoneFieldTarget = {
  fieldName: string
  scope: FieldAccessScopePath['kind']
}

/** Resolve every workflow-owned zone field copied into a new ticket version. */
export function resolveTicketVersionZoneFields(
  sourceTicket: LibraTicket,
  sourceWorkflow: LibraWorkflow | undefined
): TicketVersionZoneFieldsResult {
  if (!sourceWorkflow)
    return { status: 'unavailable', reason: 'workflow-unavailable' }
  if (sourceWorkflow.id !== sourceTicket.workflowId)
    return { status: 'unavailable', reason: 'workflow-mismatch' }

  const zoneFieldTargets = new Map<string, TicketVersionZoneFieldTarget>()
  const addZoneFieldTarget = (
    fieldName: string,
    scope: FieldAccessScopePath['kind']
  ) => zoneFieldTargets.set(`${scope}:${fieldName}`, { fieldName, scope })

  for (const step of sourceWorkflow.steps) {
    if (step.component === ZONE_ANALYSIS_STEP_COMPONENT) {
      const config = zoneAnalysisConfigSchema.safeParse(step.config)
      if (!config.success)
        return { status: 'unavailable', reason: 'invalid-config' }
      addZoneFieldTarget(config.data.zoneFieldName, 'ticket')
    }

    if (
      step.component !== CONDITIONAL_FORM_STEP_COMPONENT &&
      step.component !== NII_PLANNING_PREPARATION_STEP_COMPONENT
    )
      continue
    const sections = step.config.sections
    if (sections === undefined) continue
    if (!Array.isArray(sections))
      return { status: 'unavailable', reason: 'invalid-config' }

    for (const section of sections) {
      if (section === null || typeof section !== 'object')
        return { status: 'unavailable', reason: 'invalid-config' }
      if (!('type' in section) || section.type !== 'zones') continue

      const config = ZonesSectionSchema.safeParse(section)
      if (!config.success)
        return { status: 'unavailable', reason: 'invalid-config' }
      addZoneFieldTarget(
        config.data.zoneFieldName,
        step.requiresComponentScope ? 'component' : 'ticket'
      )
    }
  }

  const captureConfig = sourceWorkflow.globalContext.planningSourceCapture
  if (captureConfig !== undefined) {
    const capture = planningSourceCaptureSchema.safeParse(captureConfig)
    if (!capture.success)
      return { status: 'unavailable', reason: 'invalid-config' }
    addZoneFieldTarget(capture.data.sourceZonesField, 'ticket')
  }

  return {
    status: 'ready',
    zoneFieldTargets: [...zoneFieldTargets.values()],
  }
}
